import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { 
  Keyboard, 
  Search, 
  Plus, 
  Home, 
  FileText, 
  Settings, 
  LogOut, 
  Moon, 
  Sun, 
  Users, 
  BarChart3,
  ClipboardList
} from "lucide-react";

interface KeyboardShortcut {
  category: string;
  shortcuts: {
    keys: string[];
    description: string;
    icon?: React.ReactNode;
  }[];
}

const shortcuts: KeyboardShortcut[] = [
  {
    category: "ການນຳທາງ (Navigation)",
    shortcuts: [
      { keys: ["Alt", "D"], description: "ໄປໜ້າ Dashboard", icon: <Home className="h-4 w-4" /> },
      { keys: ["Alt", "C"], description: "ໄປລາຍການລູກຄ້າ", icon: <Users className="h-4 w-4" /> },
      { keys: ["Alt", "L"], description: "ໄປຄຳຂໍສິນເຊື່ອ", icon: <ClipboardList className="h-4 w-4" /> },
      { keys: ["Alt", "R"], description: "ໄປລາຍງານ", icon: <BarChart3 className="h-4 w-4" /> },
      { keys: ["Alt", "S"], description: "ໄປຕັ້ງຄ່າລະບົບ", icon: <Settings className="h-4 w-4" /> },
      { keys: ["Ctrl", "N"], description: "ເພີ່ມລູກຄ້າໃໝ່", icon: <Plus className="h-4 w-4" /> },
    ],
  },
  {
    category: "ການຄົ້ນຫາ (Search)",
    shortcuts: [
      { keys: ["Ctrl", "K"], description: "ເປີດການຊອກຫາ", icon: <Search className="h-4 w-4" /> },
      { keys: ["/"], description: "ໂຟກັດໃສ່ການຊອກຫາ", icon: <Search className="h-4 w-4" /> },
      { keys: ["↑", "↓"], description: "ເລືອກຜົນການຊອກຫາ" },
      { keys: ["Enter"], description: "ເປີດລາຍການທີ່ເລືອກ" },
      { keys: ["Esc"], description: "ປິດການຊອກຫາ" },
    ],
  },
  {
    category: "ທົ່ວໄປ (General)",
    shortcuts: [
      { keys: ["Alt", "T"], description: "ປ່ຽນ Dark/Light Mode", icon: <Moon className="h-4 w-4" /> },
      { keys: ["Ctrl", "/"], description: "ເປີດໜ້າຊ່ວຍເຫຼືອນີ້", icon: <Keyboard className="h-4 w-4" /> },
      { keys: ["Shift", "?"], description: "ສະແດງ Keyboard Shortcuts", icon: <Keyboard className="h-4 w-4" /> },
      { keys: ["Esc"], description: "ປິດ Dialog/Modal" },
      { keys: ["Ctrl", "S"], description: "ບັນທຶກຟອມ" },
    ],
  },
  {
    category: "ຕາຕະລາງ (Tables)",
    shortcuts: [
      { keys: ["Ctrl", "A"], description: "ເລືອກທັງໝົດ" },
      { keys: ["Ctrl", "E"], description: "Export ຂໍ້ມູນ" },
      { keys: ["Ctrl", "F"], description: "ຊອກຫາໃນຕາຕະລາງ" },
    ],
  },
];

export function HelpModal() {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl+/ or Cmd+/ to open help modal
      if ((e.ctrlKey || e.metaKey) && e.key === "/") {
        e.preventDefault();
        setIsOpen(true);
      }
      // Shift+? to open help modal
      if (e.shiftKey && e.key === "?") {
        e.preventDefault();
        setIsOpen(true);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-2xl">
            <Keyboard className="h-6 w-6" />
            ຄູ່ມືການໃຊ້ Keyboard Shortcuts
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          {shortcuts.map((category, idx) => (
            <div key={idx} className="space-y-3">
              <h3 className="text-lg font-semibold text-foreground/90 border-b pb-2">
                {category.category}
              </h3>
              <div className="space-y-2">
                {category.shortcuts.map((shortcut, sidx) => (
                  <div
                    key={sidx}
                    className="flex items-center justify-between p-3 rounded-lg hover:bg-accent/50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      {shortcut.icon && (
                        <div className="text-muted-foreground">
                          {shortcut.icon}
                        </div>
                      )}
                      <span className="text-sm text-foreground/80">
                        {shortcut.description}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      {shortcut.keys.map((key, kidx) => (
                        <span key={kidx} className="flex items-center gap-1">
                          <Badge
                            variant="outline"
                            className="px-2 py-1 text-xs font-mono bg-background"
                          >
                            {key}
                          </Badge>
                          {kidx < shortcut.keys.length - 1 && (
                            <span className="text-muted-foreground text-xs">+</span>
                          )}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}

          <div className="mt-6 p-4 bg-muted/50 rounded-lg">
            <p className="text-sm text-muted-foreground">
              💡 <strong>ຄຳແນະນຳ:</strong> ໃຊ້ <Badge variant="outline" className="mx-1 px-2 py-0.5 text-xs font-mono">Ctrl</Badge> + <Badge variant="outline" className="mx-1 px-2 py-0.5 text-xs font-mono">/</Badge> ຫຼື <Badge variant="outline" className="mx-1 px-2 py-0.5 text-xs font-mono">Shift</Badge> + <Badge variant="outline" className="mx-1 px-2 py-0.5 text-xs font-mono">?</Badge> ເພື່ອເປີດໜ້ານີ້ໄດ້ທຸກເວລາ
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
